import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LangContext } from '../../contexts/langContext';
import { SocialContext } from '../../contexts/socialContext';
import { StateContext } from '../../contexts/stateContext';
import { default_design } from '../../static/default_design';
import { nakshatra_yoni_dictionary, yoni_dictionary } from '../../static/jyotish_local';
import { getYoniCompatibility } from '../../static/jyotish_utils';
import { getJyotish } from '../Edu/utilsJyotish';
import { Background } from '../UiKit/Background';
import { Icon } from '../UiKit/Icon';
import { Preview } from '../UiKit/Preview';
import { Button } from '../UiKit/Button';
import { UserDropDown } from '../UiKit/UserDropDown';
import { WithVersion } from '../UiKit/VersionToggler';
import { TooltipH } from '../UiKit/TooltipH';
import { parseDesignInPersonas } from '../utilsDesign';
import {
  getPentaDesign
} from '../utilsPenta';
import { PentaBody } from './PentaBody';
import { PentaName } from './PentaName';
import { PentaStats } from './PentaStats';


const resolveYoniEntry = (yoniId) => {
  if (!yoniId) return null;
  return yoni_dictionary[yoniId]
    || Object.values(yoni_dictionary).find((y) => y.id === yoniId)
    || null;
};

const getYoniInfoForAvatar = (avatar) => {
  if (!avatar?.dob || !avatar?.aggr?.city?.location) return null;
  try {
    const result = getJyotish(avatar);
    const nakshatra = result?.kundli?.dasha?.birthNakshatra;
    if (!nakshatra || typeof nakshatra !== 'string') return null;
    const normalized = nakshatra.toLowerCase().replace(/\s+/g, '_');
    const yoniId = nakshatra_yoni_dictionary[normalized]?.yoniId || null;
    const entry = resolveYoniEntry(yoniId);
    return {
      id: yoniId,
      emoji: entry?.emoji || nakshatra_yoni_dictionary[normalized]?.yoniEmoji || null,
    };
  } catch {
    return null;
  }
};

const YONI_COMPAT_ICON = {
  match: { icon: 'favorite', titleKey: 'jyotish_compat_match' },
  mismatch: { icon: 'heart_broken', titleKey: 'jyotish_compat_mismatch' },
  neutral: { icon: 'remove', titleKey: 'jyotish_compat_neutral' },
};


export const PentaContainer = ({
  active,
  peoplePanel,
  showStats = false,
  setExpanded = () => void (0),
  penta = {},
  quantity,
  user = {},
  avatar = {},
  handleClick,
  handleChoose = () => void (0),
  dropdownActions = ['deletePenta'],
  mode = 'hd',
}) => {
  // console.log('penta = ', penta);
  const context = { ...useContext(SocialContext), ...useContext(StateContext), ...useContext(LangContext) };
  const $ui = context.$ui;
  const bodyContainer = useRef(null);
  const navigate = useNavigate();
  const isYoni = mode === 'yoni';
  const pentaPath = (token) => (isYoni ? `/yoni/p/${token}` : `/p/${token}`);

  const { pentaId } = useParams();

  const [pentaInner, setPentaInner] = useState(() => ({
    ...penta,
    members: parseDesignInPersonas(penta.members || []),
  }))

  const normalizePenta = (pentaData = {}) => {
    const normalized = {
      ...pentaData,
      members: parseDesignInPersonas(pentaData.members || []),
    };
    if (!normalized.pentaDesign && normalized.members.length >= 2) {
      normalized.pentaDesign = getPentaDesign(normalized);
    }
    return normalized;
  };

  useEffect(() => {
    if (!penta.token) return;
    const updated = context.pentas?.find(one => one.token === penta.token);
    if (updated) {
      setPentaInner(normalizePenta(updated));
    }
  }, [context.pentas, penta.token])

  useEffect(() => {
    setPentaInner(normalizePenta(penta))
  }, [penta])

  const bodyContainerThumbs = {
    thumb0: useRef(null),
    thumb1: useRef(null)
  };

  const memberYonis = useMemo(
    () => (pentaInner.members || []).map((m) => getYoniInfoForAvatar(m.avatar)),
    [pentaInner.members],
  );

  const yoniCompat = useMemo(() => {
    if (!isYoni || pentaInner.members?.length !== 2) return null;
    const [a, b] = memberYonis;
    if (!a?.id || !b?.id) return null;
    return getYoniCompatibility(
      a.id,
      b.id,
      pentaInner.members[0]?.avatar?.gender,
      pentaInner.members[1]?.avatar?.gender,
    );
  }, [isYoni, memberYonis, pentaInner.members]);

  handleClick = handleClick ? handleClick : () => {
    if (pentaInner.members.length > 1) {
      navigate(pentaPath(pentaInner.token));
      context.setState.showPeoplePanel();
    } else {
      handleChoose(pentaInner.token)
    }
  }

  const openPenta = () => {
    navigate(pentaPath(pentaInner.token));
    context.setState.showPeoplePanel();
  };

  const renderYoniEmoji = (memberIndex) => {
    const info = memberYonis[memberIndex];
    return (
      <div className="x_penta_yoni_emoji" aria-hidden={!info?.emoji}>
        {info?.emoji || <Icon size={40}>question_mark</Icon>}
      </div>
    );
  };

  const renderYoniCompatCenter = () => {
    const status = yoniCompat?.status || 'neutral';
    const meta = YONI_COMPAT_ICON[status] || YONI_COMPAT_ICON.neutral;
    const scoreText = typeof yoniCompat?.score === 'number' && yoniCompat?.maxScore
      ? `${yoniCompat.score}/${yoniCompat.maxScore}`
      : null;

    return (
      <div
        className={`x_penta_table x_penta_table--yoni x_penta_table--yoni-${status}${pentaId && pentaInner.token === pentaId ? ' x_penta_table--active' : ''}`}
        onClick={openPenta}
      >
        <TooltipH title={$ui(meta.titleKey)} placement="bottom">
          <div className="x_penta_yoni_compat">
            <div className="x_penta_yoni_compat_icon">
              <Icon size={40}>{meta.icon}</Icon>
            </div>
            {scoreText && (
              <div className="x_penta_yoni_compat_score">{scoreText}</div>
            )}
          </div>
        </TooltipH>
      </div>
    );
  };


  const deleteFromPenta = (token) => {
    const newPentaInner = JSON.parse(JSON.stringify(pentaInner))
    newPentaInner.members = parseDesignInPersonas(newPentaInner.members)
    newPentaInner.members = newPentaInner.members.filter(one => one.avatar.token !== token);
    setPentaInner(newPentaInner);
    context.x.deleteFromPenta(pentaInner.token, token)
  }

  const slotPreviewProps = {
    lines: false,
    bg: true,
    aura: true,
    width: 50,
    height: 70,
    png: true,
  };

  const renderMemberSlotPreview = (member, thumbRef) => (
    <div className="x_penta_body" ref={thumbRef}>
      <div className="x_bodygraph x_bodygraph--png">
        <Preview
          {...slotPreviewProps}
          design={member.avatar.design}
          avatarToken={member.avatar.token}
          avatarUpdated={member.avatar.updated}
        />
      </div>
    </div>
  );


  return (<>
    {pentaInner && <>
      <div
        className={`x_choose_item`}
      // className={`x_choose_item ${(!context.state.peoplePanel && pentaInner.members.length < 2) ? 'x_choose_item--empty' : ''}`}
      >
        <div className={`${context.state.peoplePanel ? "x_penta0 x_penta0--v" : "x_penta0"}${isYoni ? ' x_penta0--yoni' : ''}`}>


          <UserDropDown
            mobileIconsOnly
            penta={pentaInner}
            listId={pentaInner.token}
            // avatar={avatar}
            // user={user}
            actions={dropdownActions}

            addComponentFirst={
              <WithVersion>
                <Button
                  className="_f100"
                  sm
                  ghost
                  icon="person_add"
                  labelMobileOff
                  onClick={() => handleChoose(pentaInner.token)}
                >
                  {$ui('penta_add_people')}
                </Button>
              </WithVersion>
            }

          />


          {Boolean(pentaInner.members.length > 3) && <>
            <div className="x_tap"
              onClick={openPenta}
            />
          </>}

          <div className={`${context.state.peoplePanel ? "x_penta_table0 x_penta_table0--v" : "x_penta_table0"} ${pentaInner.members.length > 3 && 'x_penta_table0--4'}${isYoni ? ' x_penta_table0--yoni' : ''}`}>

            {pentaInner.members.length < 4 && (
              isYoni && pentaInner.members.length === 2
                ? renderYoniCompatCenter()
                : (
                  <div className={pentaId && pentaInner.token === pentaId ? "x_penta_table x_penta_table--active" : "x_penta_table"}>
                    {!isYoni && pentaInner.members?.[0] && <PentaBody
                      // lines={context.pentas.length > 5 ? false : true}
                      penta={pentaInner}
                      members={pentaInner.members}
                      parentContainer={bodyContainer}
                      className='x_penta_mix'
                      handleClick={() => handleClick(pentaInner)}
                      png={context.state.png}
                    />}

                    {!isYoni && pentaInner.members?.[0]
                      && <Background design={pentaInner.pentaDesign}
                    />}


                    {!pentaInner.members.length && <>
                      <Preview
                        lines={false}
                        aura={true}
                        parentContainer={bodyContainer}
                        className='x_penta_mix'
                        design={default_design}
                        handleClick={() => { handleChoose(pentaInner.token) }} />
                    </>}

                  </div>
                )
            )}

            {/* SLOTS */}
            {Boolean(pentaInner.members.length < 4) && pentaInner.members.map((one, index) => {
              // console.log('one.avatar.design = ', one.avatar.design);
              return (<div
                key={`member_${one.avatar.token}`}
                // style={{ overflow: 'hidden' }}
                className={`x_penta_slot x_penta_slot_q--${pentaInner.members.length} x_penta_slot--${index + 1}${isYoni ? ' x_penta_slot--yoni' : ''}`}
              >
                <div
                  className={pentaId && pentaInner.token === pentaId ? "x_click active" : "x_click"}
                  onClick={openPenta} />
                {peoplePanel && <div className="x_penta_slot_x" onClick={() => deleteFromPenta(one.avatar.token)}>
                  <div className="t_icon t_icon--20">close</div>
                </div>}
                {isYoni
                  ? renderYoniEmoji(index)
                  : renderMemberSlotPreview(one, bodyContainerThumbs[`thumb${index}`])}
              </div>);
            })}

            {Boolean(pentaInner.members.length > 3) && pentaInner.members.map((one, index) => {
              // console.log('one = ', one);

              return (<div
                key={`member_${one.avatar.token}`}
                // style={{ overflow: 'hidden' }}
                className={`x_penta_slot x_penta_slot_q--${pentaInner.members.length} ${pentaInner.members.length > 3 && 'x_penta_slot--4'} ${pentaInner.members.length > 4 && 'x_penta_slot--5'}${isYoni ? ' x_penta_slot--yoni' : ''}`}
              >
                <div
                  className={pentaId && pentaInner.token === pentaId ? "x_click active" : "x_click"}
                  onClick={openPenta} />
                {/* {peoplePanel && <div className="x_penta_slot_x" onClick={() => deleteFromPenta(one.avatar.token)}>
                  <div className="t_icon t_icon--20">close</div>
                </div>} */}
                {isYoni
                  ? renderYoniEmoji(index)
                  : renderMemberSlotPreview(one, bodyContainerThumbs[`thumb${index}`])}
              </div>);
            })}




            {pentaInner.members.length === 0 && <>
              <div className="x_penta_slot x_penta_slot--1 x_penta_slot--empty"
                key={'slot1'}
                onClick={() => handleChoose(pentaInner.token)}
              >
                <div className="t_icon t_icon--40">person_add</div>
              </div>
            </>}
            {pentaInner.members.length < 2 && <>
              <div className="x_penta_slot x_penta_slot--2 x_penta_slot--empty"
                key={'slot2'}
                onClick={() => handleChoose(pentaInner.token)}
              >
                <div className="t_icon t_icon--40">person_add</div>
              </div>
            </>}
          </div>


          <div className="x_penta_name">
            <PentaName
              penta={pentaInner}
            ><div><Icon>edit</Icon></div>
            </PentaName>
          </div>

          {!isYoni && (showStats || context.state.peoplePanel) && pentaInner.members.length === 2 && <PentaStats penta={pentaInner} />}



        </div>
      </div>
    </>}
  </>
  );
};
